import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { IVehicle } from "@app/interfaces/IVehicle";
import { EngineerJobItemStatus, EngineerJobItemType } from "@app/main/modules/foundation/foundation.enums";
import { IEngineerCommission, IEngineerJobItem } from "@app/main/modules/foundation/foundation.interfaces";
import { EngineerJobItemService } from "@app/main/modules/foundation/_services/engineer-job-item.service";
import { EngineerJobService } from "@app/main/modules/foundation/_services/engineer-job.service";
import { FoundationVehiclesService } from "@app/main/modules/foundation/_services/vehicle.service";
import { NavigationService } from "@app/services/navigation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable } from "rxjs";
import { take, tap, finalize, switchMap } from "rxjs/operators";
import { FoundationNavigationModule } from "../../../foundation.navigation";
import { EngineerCommissionService } from "../../../_services/engineer-commission.service";
import './job-item-editor.component.scss';

@UntilDestroy()
@Component({
    selector: 'job-item-editor',
    template: require('./job-item-editor.component.html')
})
export class JobItemEditorComponent implements OnInit, OnChanges, OnDestroy {
    @Input() jobItem: IEngineerJobItem;
    @Input() allJobItems: IEngineerJobItem[];
    @Input() duplicateJobId: number;

    @Output() jobItemSaved = new EventEmitter<void>();
    @Output() vehicleSelected = new EventEmitter<IVehicle>();
    @Output() lastJobItemCancelled = new EventEmitter<void>();
    @Output() vehicleAdd = new EventEmitter<IVehicle>();
    @Output() reportIssueClick = new EventEmitter<void>();

    //--

    accountId: number;
    amendedJobItem: IEngineerJobItem;

    commission: IEngineerCommission;

    showVehicleAddForm = false;
    vehicles: IVehicle[];
    availableVehiclesPromise: Promise<IVehicle[]>;
    availableVehicles: IVehicle[];
    selectedVehicle: IVehicle;

    jobItemEditorForm: FormGroup;
    vehicleAddForm: FormGroup;
    vehicleToAdd: IVehicle = {};
    noVehicleAvailable: boolean = false;

    vehiclesLoading: boolean;

    isLastJobItem: boolean = false;

    engineerJobItemStatus = EngineerJobItemStatus;
    statuses = Object.keys(EngineerJobItemStatus).filter(s => !isNaN(Number(s))).map(s => Number(s));
    engineerJobItemType = EngineerJobItemType;
    types = Object.keys(EngineerJobItemType).filter(s => !isNaN(Number(s))).map(s => Number(s));

    saving: boolean = false;


    constructor(
        private readonly engineerCommissionService: EngineerCommissionService,
        private readonly engineerJobService: EngineerJobService,
        private readonly engineerJobItemService: EngineerJobItemService,
        private readonly navigationService: NavigationService,
        private readonly vehicleService: FoundationVehiclesService) { }

    ngOnInit(): void {
        this.getData();
        this.loadCommissions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['jobItem']) {

            this.mapPropertiesToAmendedJobItem();
            this.setupForms();
        }

        if (changes['allJobItems']) {
            this.isLastJobItem = this.getIsLastJobItem();
        }
    }

    ngOnDestroy(): void {
        delete this.amendedJobItem;
    }

    showAddVehicle(): void {
        this.showVehicleAddForm = true;
        this.clearVehicle();
    }

    clearVehicle(): void {
        this.selectedVehicle = null;
        this.amendedJobItem.VehicleId = null;
        this.vehicleSelected.emit(null);
    }


    saveJobItem(): void {
        this.saving = true;

        this.engineerJobItemService.createUpdateEngineerJobItem(this.amendedJobItem)
            .pipe(take(1))
            .subscribe(jobItem => {
                this.jobItemSaved.emit();
                this.saving = false;
            });
    }

    onVehicleSelected(vehicle: IVehicle): void {
        this.selectedVehicle = vehicle;
        this.amendedJobItem.VehicleId = vehicle.Id;
        this.vehicleSelected.emit(vehicle);
    }

    vehicleSearchPredicate(vehicle: IVehicle, text: string): boolean {
        return vehicle.ShortReg.toLowerCase().includes(text.toLowerCase()) || vehicle.ShortReg.toLowerCase().includes(text.toLowerCase());
    }

    onNewVehicleAdded(vehicle: IVehicle): void {
        this.selectedVehicle = vehicle;
        this.amendedJobItem.VehicleId = vehicle.Id;
        this.amendedJobItem.vehicle = vehicle;
        this.setNoVehicle(false);
        this.cancelAddVehicle();
        this.vehicleAdd.emit(vehicle);
    }

    setNoVehicle(noVehicle: boolean): void {
        this.noVehicleAvailable = noVehicle;
    }

    cancelJobItem(cancel: boolean): void {
        if (!cancel) {
            return;
        }

        if (this.isLastJobItem) {
            this.lastJobItemCancelled.emit();
            return;
        }
        this.engineerJobItemService.closeEngineerJobItem(this.amendedJobItem).pipe(take(1)).subscribe();

        this.jobItemSaved.emit();
    }


    cancelAddVehicle(): void {
        this.showVehicleAddForm = false;
    }

    addCommission(): void {
        this.navigationService.go(FoundationNavigationModule.NewCommissionWithBindings, { jobItemId: this.jobItem.Id, customerId: this.accountId });
    }

    reportIssue(): void {
        this.reportIssueClick.emit();
    }

    viewCommission(): void {
        if (!this.amendedJobItem.commission) {
            return;
        }

        this.navigationService.go(FoundationNavigationModule.ViewCommission, { commissionId: this.amendedJobItem.commission.Id });
    }

    get canSaveForm(): boolean {
        return this.amendedJobItem.Status === EngineerJobItemStatus.Incomplete
            && (!!this.selectedVehicle || this.noVehicleAvailable)
            && !this.saving;

    }

    get canMarkUnknown(): boolean {
        return (this.amendedJobItem.Type === EngineerJobItemType.Install
            || this.amendedJobItem.Type === EngineerJobItemType.Reinstall)
            && this.amendedJobItem.Status === EngineerJobItemStatus.Incomplete;
    }

    //--

    private setupForms(): void {
        this.setupJobItemEditorForm();
        this.setupVehicleAddForm();
    }

    private loadCommissions(): void {
        this.engineerCommissionService.getEngineerCommissions({ JobId: this.jobItem.JobId })
            .pipe(untilDestroyed(this))
            .subscribe(commissions => {
                this.amendedJobItem.commission = commissions.find(commission => commission.JobItemId == this.jobItem.Id);
            })
    }

    private mapPropertiesToAmendedJobItem(): void {
        this.amendedJobItem = { ...this.jobItem };
        this.selectedVehicle = this.jobItem.vehicle;

        if (!this.amendedJobItem.VehicleId && this.amendedJobItem.Id) {
            this.noVehicleAvailable = true;
        }
    }

    private setupJobItemEditorForm(): void {
        const instructions = this.amendedJobItem?.Comments || "";

        this.jobItemEditorForm = new FormGroup({
            'Instructions': new FormControl(instructions)
        });

        this.jobItemEditorForm.statusChanges.pipe(untilDestroyed(this)).subscribe((status: string) => {
            if (status === "INVALID") {
                return;
            }

            this.amendedJobItem = {
                ...this.amendedJobItem,
                Comments: this.jobItemEditorForm.value.Instructions
            }
        });
    }

    private getData(): void {
        this.engineerJobService.getEngineerJob(this.jobItem.JobId).pipe(
            switchMap(job => {
                this.accountId = job.AccountId;
                return this.getVehicles(job.AccountId);
            })
        ).subscribe({
            next: () => {
                this.setupForms();
                this.vehiclesLoading = false;
            }
        });
    }

    private getVehicles(accountId: number): Observable<IVehicle[]> {
        this.vehiclesLoading = true;
        return this.vehicleService.getVehiclesForAccount(accountId)
            .pipe(
                untilDestroyed(this),
                tap(vehicles => {
                    this.vehicles = vehicles;
                })
            )
    }

    private setupVehicleAddForm(): void {
        this.vehicleAddForm = new FormGroup({
            "Reg": new FormControl("", [Validators.required, CustomFormValidators.duplicateVehicle(this.vehicles)]),
            "Make": new FormControl(""),
            "Model": new FormControl("")
        });

        this.vehicleAddForm.statusChanges.pipe(untilDestroyed(this)).subscribe((status: string) => {
            if (status === "INVALID") {
                return;
            }

            this.vehicleToAdd = {
                ...this.vehicleToAdd,
                AccountId: this.accountId,
                Reg: this.vehicleAddForm.value.Reg,
                ShortReg: this.vehicleAddForm.value.Reg,
                Make: this.vehicleAddForm.value.Make,
                Model: this.vehicleAddForm.value.Model,
            }
        })
    }

    private getIsLastJobItem(): boolean {
        const openItems = this.allJobItems?.filter(jobItem => jobItem.Status === EngineerJobItemStatus.Incomplete);

        return openItems.length === 1;
    }
}
